import {useEffect, useState, useMemo} from 'react';
import {apiGetDepotStatistics} from 'constants/Api';
import {IDepotStatistics} from 'constants/interfaces';

/* extra fields a few widgets expect -------------------------------- */
type IInvoiceExtras = {
  paidInvoicesTotal?: number;
  invoicesTotal?: number;
  quotesTotal?: number;
  inProgressInvoicesCount?: number;
};

export type IExtendedDepotStats = IDepotStatistics & IInvoiceExtras;

/* helpers ---------------------------------------------------------- */
const cache = new Map<string, IExtendedDepotStats>();

const toDate = (d: Date | string): Date => {
  if (d instanceof Date) {
    // Check if date is valid
    if (isNaN(d.getTime())) {
      console.warn(
        'Invalid date provided to useDepotStats, using current date',
      );
      return new Date();
    }
    return d;
  }
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) {
    console.warn(
      'Invalid date string provided to useDepotStats, using current date',
    );
    return new Date();
  }
  return parsed;
};

const keyFor = (from: Date | string, to: Date | string) =>
  `${toDate(from).toISOString().slice(0, 10)}..${toDate(to)
    .toISOString()
    .slice(0, 10)}`;

/* default stats to use when API fails or returns no data */
const getDefaultStats = (): IExtendedDepotStats =>
  ({
    invoicesCount: 0,
    paidInvoicesCount: 0,
    unpaidInvoicesCount: 0,
    quotesCount: 0,
    paidInvoicesTotal: 0,
    invoicesTotal: 0,
    quotesTotal: 0,
    inProgressInvoicesCount: 0,
  } as IExtendedDepotStats);

/* hook ------------------------------------------------------------- */
/* eslint-disable-next-line @typescript-eslint/naming-convention */
export default function useDepotStats(
  fromRaw: Date | string,
  toRaw: Date | string,
): {loading: boolean; stats?: IExtendedDepotStats} {
  // Memoize the dates and key to prevent unnecessary re-renders
  const {from, to, key} = useMemo(() => {
    const fromDate = toDate(fromRaw);
    const toDateValue = toDate(toRaw);
    const cacheKey = keyFor(fromDate, toDateValue);
    return {from: fromDate, to: toDateValue, key: cacheKey};
  }, [
    fromRaw instanceof Date ? fromRaw.getTime() : fromRaw,
    toRaw instanceof Date ? toRaw.getTime() : toRaw,
  ]);

  // Check cache synchronously on each render
  const cachedStats = cache.get(key);
  const [stats, setStats] = useState<IExtendedDepotStats | undefined>(
    cachedStats,
  );
  const [loading, setLoading] = useState<boolean>(!cachedStats);

  useEffect(() => {
    let alive = true;
    let timeoutId: NodeJS.Timeout | null = null;

    /* ---- immediate cache hit ------------------------------------ */
    const cached = cache.get(key);
    if (cached) {
      // If we have cached data, use it immediately
      setStats(cached);
      setLoading(false);
      return; // nothing else to do
    }

    /* ---- otherwise, fetch & cache ------------------------------- */
    setLoading(true);

    // Set a timeout to prevent infinite loading (10 seconds)
    timeoutId = setTimeout(() => {
      if (alive) {
        console.warn(
          `Depot statistics fetch timeout for key ${key}, using default stats`,
        );
        const defaultStats = getDefaultStats();
        setStats(defaultStats);
        setLoading(false);
      }
    }, 10000);

    (async () => {
      try {
        const raw = await apiGetDepotStatistics(from, to);

        if (!alive) return;

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        const extras: IInvoiceExtras = {
          paidInvoicesTotal: raw.paidInvoicesTotal ?? 0,
          invoicesTotal: raw.invoicesTotal ?? 0,
          quotesTotal: raw.quotesTotal ?? 0,
          inProgressInvoicesCount: (raw as any).inProgressInvoicesCount ?? 0,
        };

        const merged = {...raw, ...extras};
        cache.set(key, merged);

        setStats(merged);
        setLoading(false);
      } catch (error) {
        if (!alive) return;

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        console.error('Error fetching depot statistics:', error);
        // Set default empty stats to prevent infinite skeleton loading
        const defaultStats = getDefaultStats();
        setStats(defaultStats);
        setLoading(false);
        // Don't cache errors, so next time it will retry
      }
    })();

    return () => {
      alive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [key]); // Only depend on key, which is stable

  // Always return stats (never undefined) to prevent infinite skeleton loading
  return {loading, stats: stats || getDefaultStats()};
}
